import { baseApi } from "../baseApi";

const SHIPMENT_REFUND_BASE_URL = "/shipment-refund";

export const shipmentRefundApi = baseApi.injectEndpoints({
  tagTypes: ["ShipmentRefund"],

  endpoints: (build) => ({
    addShipmentRefund: build.mutation({
      query: (body) => ({
        url: SHIPMENT_REFUND_BASE_URL,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "ShipmentRefund", id: "LIST" },
        { type: "ShipmentRefund", id: "SHIPMENT_DDL" },
      ],
    }),

    listShipmentRefunds: build.query({
      query: (params) => ({
        url: SHIPMENT_REFUND_BASE_URL,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({
                type: "ShipmentRefund",
                id: _id,
              })),
              { type: "ShipmentRefund", id: "LIST" },
            ]
          : [{ type: "ShipmentRefund", id: "LIST" }],
    }),

    getDDLShipmentRefundList: build.query({
      query: (params) => ({
        url: `${SHIPMENT_REFUND_BASE_URL}/ddl`,
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({
                type: "ShipmentRefund",
                id: _id,
              })),
              { type: "ShipmentRefund", id: "DDL" },
            ]
          : [{ type: "ShipmentRefund", id: "DDL" }],
    }),

    getShipmentRefundDetail: build.query({
      query: (id) => `${SHIPMENT_REFUND_BASE_URL}/${id}`,
      providesTags: (result, error, id) => [
        { type: "ShipmentRefund", id },
        { type: "ShipmentRefund", id: "LIST" },
      ],
    }),

    getShipmentRefundEBills: build.query({
      query: (id) => `${SHIPMENT_REFUND_BASE_URL}/${id}/ebills`,
      providesTags: (result, error, id) => [
        { type: "ShipmentRefund", id },
        { type: "ShipmentRefund", id: "LIST" },
      ],
    }),

    printShipmentRefundCreditNote: build.mutation({
      query: ({ creditNoteId }) => ({
        url: `${SHIPMENT_REFUND_BASE_URL}/credit-notes/${creditNoteId}/print`,
        method: "POST",
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: "ShipmentRefund", id: result._id },
              { type: "ShipmentRefund", id: "LIST" },
            ]
          : [{ type: "ShipmentRefund", id: "LIST" }],
    }),

    printShipmentRefundInvoice: build.mutation({
      query: ({ invoiceId }) => ({
        url: `${SHIPMENT_REFUND_BASE_URL}/invoices/${invoiceId}/print`,
        method: "POST",
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: "ShipmentRefund", id: result._id },
              { type: "ShipmentRefund", id: "LIST" },
            ]
          : [{ type: "ShipmentRefund", id: "LIST" }],
    }),

    updateShipmentRefund: build.mutation({
      query: ({ _id, ...body }) => ({
        url: `${SHIPMENT_REFUND_BASE_URL}/${_id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { _id: id }) => [
        { type: "ShipmentRefund", id },
        { type: "ShipmentRefund", id: "LIST" },
        { type: "ShipmentRefund", id: "SHIPMENT_DDL" },
      ],
    }),
    getDDLShipmentForRefund: build.query({
      query: (params) => ({
        url: `${SHIPMENT_REFUND_BASE_URL}/ddl/shipments`,
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({
                type: "ShipmentRefund",
                id: _id,
              })),
              { type: "ShipmentRefund", id: "SHIPMENT_DDL" },
            ]
          : [{ type: "ShipmentRefund", id: "SHIPMENT_DDL" }],
    }),

    getDDLShipmentForRefundLite: build.query({
      query: (params) => ({
        url: `${SHIPMENT_REFUND_BASE_URL}/ddl/shipments/lite`,
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({
                type: "ShipmentRefund",
                id: _id,
              })),
              { type: "ShipmentRefund", id: "SHIPMENT_DDL" },
            ]
          : [{ type: "ShipmentRefund", id: "SHIPMENT_DDL" }],
    }),

    getDDLShipmentPriceList: build.query({
      query: ({ shipmentId, ...params }) => ({
        url: `${SHIPMENT_REFUND_BASE_URL}/${shipmentId}/prices/ddl`,
        params: { ...params, ddl: true },
      }),
      transformResponse: (response) =>
        Array.isArray(response)
          ? { prices: response, totalCharge: 0 }
          : {
              prices: response?.prices ?? response?.data ?? [],
              totalCharge: response?.totalCharge ?? 0,
            },
      providesTags: (_result, _error, { shipmentId }) => [
        { type: "ShipmentPrice", id: shipmentId },
        { type: "ShipmentPrice", id: "DDL" },
      ],
    }),

    approveShipmentRefund: build.mutation({
      query: ({ _id, ...body }) => ({
        url: `${SHIPMENT_REFUND_BASE_URL}/${_id}/approve`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { _id: id }) => [
        { type: "ShipmentRefund", id },
        { type: "ShipmentRefund", id: "LIST" },
        { type: "ShipmentRefund", id: "SHIPMENT_DDL" },
      ],
    }),

    // Finance reject
    rejectShipmentRefund: build.mutation({
      query: ({ _id, ...body }) => ({
        url: `${SHIPMENT_REFUND_BASE_URL}/${_id}/reject`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { _id: id }) => [
        { type: "ShipmentRefund", id },
        { type: "ShipmentRefund", id: "LIST" },
        { type: "ShipmentRefund", id: "SHIPMENT_DDL" },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useAddShipmentRefundMutation,
  useListShipmentRefundsQuery,
  useLazyListShipmentRefundsQuery,
  useLazyGetDDLShipmentRefundListQuery,
  useGetShipmentRefundDetailQuery,
  useUpdateShipmentRefundMutation,
  useLazyGetDDLShipmentForRefundQuery,
  useLazyGetDDLShipmentForRefundLiteQuery,
  useLazyGetDDLShipmentPriceListQuery,
  useApproveShipmentRefundMutation,
  useRejectShipmentRefundMutation,
  useGetShipmentRefundEBillsQuery,
  usePrintShipmentRefundCreditNoteMutation,
  usePrintShipmentRefundInvoiceMutation,
} = shipmentRefundApi;
